import { useState, useRef, useEffect, useCallback } from "react";
import "@/App.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const EMERGENT_AUTH_URL = "https://auth.emergentagent.com";

// Safe fetch body read (avoids "Body is disturbed or locked" in preview wrappers)
const readJson = async (res) => {
  try { return await res.clone().json(); } catch {
    try { return JSON.parse(await res.clone().text()); } catch { return {}; }
  }
};

// Auth helper: include credentials + optional bearer token
const authFetch = (url, opts = {}) => {
  const token = localStorage.getItem("session_token");
  const headers = { ...(opts.headers || {}) };
  if (token) headers["Authorization"] = `Bearer ${token}`;
  return fetch(url, { ...opts, headers, credentials: "include" });
};

// Simple device fingerprint (UA + screen + timezone + language)
const getDeviceFingerprint = () => {
  try {
    const raw = [
      navigator.userAgent,
      `${window.screen.width}x${window.screen.height}`,
      Intl.DateTimeFormat().resolvedOptions().timeZone,
      navigator.language,
      navigator.hardwareConcurrency || "",
    ].join("|");
    let hash = 0;
    for (let i = 0; i < raw.length; i++) {
      hash = ((hash << 5) - hash) + raw.charCodeAt(i);
      hash |= 0;
    }
    return `fp_${Math.abs(hash).toString(16)}`;
  } catch { return "fp_unknown"; }
};

const style = `
@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,700;0,900;1,700&family=DM+Sans:wght@300;400;500&display=swap');
* { box-sizing: border-box; margin: 0; padding: 0; }
:root {
  --gold: #C9A84C; --gold-light: #E8C96A; --gold-faint: rgba(201,168,76,0.12);
  --dark: #0A0A0A; --dark-2: #111; --dark-3: #1A1A1A;
  --white: #F5F0E8; --dim: rgba(245,240,232,0.55);
  --faint: rgba(245,240,232,0.08); --faint2: rgba(245,240,232,0.13);
}
.app { min-height:100vh; background:var(--dark); color:var(--white); font-family:'DM Sans',sans-serif; }
.grain { position:fixed; inset:0; pointer-events:none; z-index:0; opacity:0.03;
  background-image:url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");
  background-size:200px; }
.header { position:relative; z-index:10; padding:1.8rem 2.5rem; display:flex; align-items:center; justify-content:space-between; border-bottom:1px solid var(--faint); gap:1rem; }
.logo { font-family:'Playfair Display',serif; font-size:1.5rem; font-weight:900; letter-spacing:-0.02em; }
.logo span { color:var(--gold); }
.logo-sub { font-size:0.6rem; letter-spacing:0.25em; text-transform:uppercase; color:var(--dim); margin-top:2px; }
.header-right { display:flex; align-items:center; gap:0.8rem; }
.quota-badge { background:var(--gold-faint); border:1px solid rgba(201,168,76,0.3); color:var(--gold); font-size:0.7rem; letter-spacing:0.1em; text-transform:uppercase; padding:0.4rem 0.9rem; border-radius:100px; }
.quota-badge.exhausted { background:#1a0e0e; border-color:rgba(220,80,80,0.35); color:#e07070; }
.avatar { width:34px; height:34px; border-radius:50%; border:1px solid var(--faint2); object-fit:cover; cursor:pointer; }
.logout-btn { background:transparent; border:1px solid var(--faint2); color:var(--dim); font-family:'DM Sans',sans-serif; font-size:0.75rem; padding:0.4rem 0.8rem; border-radius:8px; cursor:pointer; transition:all 0.2s; }
.logout-btn:hover { border-color:var(--gold); color:var(--gold); }
.hero { position:relative; z-index:10; text-align:center; padding:3rem 2rem 2rem; }
.hero-tag { display:inline-block; font-size:0.68rem; letter-spacing:0.3em; text-transform:uppercase; color:var(--gold); margin-bottom:1rem; }
.hero h1 { font-family:'Playfair Display',serif; font-size:clamp(1.9rem,5vw,3.2rem); font-weight:900; line-height:1.1; letter-spacing:-0.03em; max-width:580px; margin:0 auto 1rem; }
.hero h1 em { font-style:italic; color:var(--gold); }
.hero p { color:var(--dim); font-size:0.92rem; font-weight:300; max-width:420px; margin:0 auto; line-height:1.7; }
.divider { width:36px; height:1px; background:var(--gold); margin:1.5rem auto 0; opacity:0.4; }
.main { position:relative; z-index:10; max-width:640px; margin:0 auto; padding:2rem 1.5rem 4rem; }
.card { background:var(--dark-2); border:1px solid var(--faint2); border-radius:18px; padding:1.8rem; margin-bottom:1rem; }
.card-label { font-size:0.67rem; letter-spacing:0.22em; text-transform:uppercase; color:var(--dim); margin-bottom:0.9rem; }
input, textarea, select { width:100%; background:var(--dark-3); border:1px solid var(--faint2); border-radius:11px; color:var(--white); font-family:'DM Sans',sans-serif; font-size:0.9rem; padding:0.82rem 1rem; outline:none; transition:border-color 0.2s; resize:none; }
input:focus, textarea:focus, select:focus { border-color:rgba(201,168,76,0.5); }
input::placeholder, textarea::placeholder { color:rgba(245,240,232,0.22); }
textarea { min-height:90px; }
.hint { font-size:0.71rem; color:var(--dim); margin-top:0.5rem; line-height:1.5; }
.hint b { color:var(--gold); font-weight:500; }
.net-grid { display:grid; grid-template-columns:repeat(2,1fr); gap:0.6rem; }
.net-btn { padding:0.75rem; border-radius:10px; border:1px solid var(--faint2); background:var(--dark-3); color:var(--dim); font-family:'DM Sans',sans-serif; font-size:0.85rem; cursor:pointer; transition:all 0.18s; text-align:center; }
.net-btn.active { background:var(--gold-faint); border-color:rgba(201,168,76,0.5); color:var(--gold); }
.gen-btn { width:100%; padding:1.15rem; background:var(--gold); border:none; border-radius:14px; color:var(--dark); font-family:'Playfair Display',serif; font-size:1.1rem; font-weight:700; cursor:pointer; transition:all 0.2s; letter-spacing:0.02em; }
.gen-btn:hover:not(:disabled) { background:var(--gold-light); transform:translateY(-2px); box-shadow:0 8px 28px rgba(201,168,76,0.22); }
.gen-btn:disabled { opacity:0.38; cursor:not-allowed; }
.loading-card { background:var(--dark-2); border:1px solid var(--faint2); border-radius:18px; padding:3.5rem 2rem; text-align:center; }
.dots { display:inline-flex; gap:7px; margin-bottom:1.2rem; }
.dot { width:9px; height:9px; background:var(--gold); border-radius:50%; animation:pulse 1.4s ease-in-out infinite; }
.dot:nth-child(2){animation-delay:0.2s} .dot:nth-child(3){animation-delay:0.4s}
@keyframes pulse { 0%,80%,100%{opacity:0.2;transform:scale(0.8)} 40%{opacity:1;transform:scale(1.1)} }
.loading-title { font-family:'Playfair Display',serif; font-size:1.2rem; margin-bottom:0.6rem; }
.loading-steps { font-size:0.8rem; color:var(--dim); line-height:2; }
.loading-steps b { color:var(--gold); }
.results-head { text-align:center; margin-bottom:1.8rem; }
.results-title { font-family:'Playfair Display',serif; font-size:1.4rem; color:var(--gold); margin-bottom:0.3rem; }
.results-sub { font-size:0.78rem; color:var(--dim); }
.conseil-card { background:var(--gold-faint); border:1px solid rgba(201,168,76,0.25); border-radius:14px; padding:1.3rem 1.4rem; margin-bottom:1rem; }
.conseil-title { font-family:'Playfair Display',serif; font-size:0.88rem; color:var(--gold); margin-bottom:0.5rem; }
.conseil-text { font-size:0.87rem; line-height:1.75; }
.result-block { background:var(--dark-2); border:1px solid var(--faint2); border-radius:14px; margin-bottom:0.9rem; overflow:hidden; }
.result-head { padding:0.85rem 1.3rem; border-bottom:1px solid var(--faint); display:flex; align-items:center; justify-content:space-between; gap:0.5rem; }
.result-label { font-family:'Playfair Display',serif; font-size:0.88rem; font-weight:700; color:var(--gold); }
.result-head-actions { display:flex; align-items:center; gap:0.4rem; flex-wrap:wrap; }
.copy-btn, .video-btn { background:var(--faint); border:1px solid var(--faint2); border-radius:7px; color:var(--dim); font-family:'DM Sans',sans-serif; font-size:0.7rem; padding:0.26rem 0.72rem; cursor:pointer; transition:all 0.2s; }
.video-btn { background:var(--gold-faint); border-color:rgba(201,168,76,0.35); color:var(--gold); }
.copy-btn:hover { color:var(--gold); border-color:rgba(201,168,76,0.4); }
.video-btn:hover { background:rgba(201,168,76,0.2); }
.copy-btn.copied { color:#6fcf97; border-color:rgba(111,207,151,0.35); }
.result-content { padding:1.2rem 1.3rem; font-size:0.87rem; line-height:1.8; white-space:pre-wrap; }
.error-card { background:#1a0e0e; border:1px solid rgba(220,80,80,0.3); border-radius:14px; padding:1.5rem; margin-bottom:1rem; color:#e07070; font-size:0.87rem; line-height:1.7; }
.reset-btn { width:100%; padding:0.88rem; background:transparent; border:1px solid var(--faint2); border-radius:12px; color:var(--dim); font-family:'DM Sans',sans-serif; font-size:0.87rem; cursor:pointer; transition:all 0.2s; margin-top:0.4rem; }
.reset-btn:hover { border-color:var(--gold); color:var(--gold); }
.footer { position:relative; z-index:10; text-align:center; padding:1.8rem; border-top:1px solid var(--faint); color:var(--dim); font-size:0.7rem; letter-spacing:0.14em; }
.video-embed { padding:1rem 1.3rem 1.3rem; border-top:1px solid var(--faint); text-align:center; }
.video-embed video { width:100%; max-width:320px; display:block; margin:0 auto 0.8rem; border-radius:10px; background:var(--dark-3); }
.video-embed a, .video-embed button.share-btn { display:inline-block; font-size:0.75rem; color:var(--gold); text-decoration:none; border:1px solid rgba(201,168,76,0.35); border-radius:7px; padding:0.4rem 0.9rem; margin:0 0.3rem; background:transparent; cursor:pointer; font-family:'DM Sans',sans-serif; transition:all 0.2s; }
.video-embed a:hover, .video-embed button.share-btn:hover { background:var(--gold-faint); }
.share-note { display:block; font-size:0.68rem; color:var(--dim); margin-top:0.6rem; line-height:1.5; }
.modal-overlay { position:fixed; inset:0; background:rgba(0,0,0,0.75); backdrop-filter:blur(6px); z-index:100; display:flex; align-items:center; justify-content:center; padding:1.5rem; }
.modal { background:var(--dark-2); border:1px solid var(--faint2); border-radius:18px; max-width:520px; width:100%; max-height:90vh; overflow-y:auto; padding:1.8rem; }
.modal-title { font-family:'Playfair Display',serif; font-size:1.3rem; color:var(--gold); margin-bottom:0.4rem; }
.modal-sub { font-size:0.78rem; color:var(--dim); margin-bottom:1.3rem; line-height:1.6; }
.modal-label { font-size:0.67rem; letter-spacing:0.22em; text-transform:uppercase; color:var(--dim); margin-bottom:0.6rem; margin-top:1rem; }
.modal textarea { min-height:140px; }
.duration-grid { display:grid; grid-template-columns:repeat(3,1fr); gap:0.5rem; }
.duration-btn { padding:0.7rem; border-radius:10px; border:1px solid var(--faint2); background:var(--dark-3); color:var(--dim); font-family:'DM Sans',sans-serif; font-size:0.85rem; cursor:pointer; transition:all 0.18s; }
.duration-btn.active { background:var(--gold-faint); border-color:rgba(201,168,76,0.5); color:var(--gold); }
.modal-actions { display:flex; gap:0.6rem; margin-top:1.4rem; }
.modal-cancel { flex:1; padding:0.85rem; background:transparent; border:1px solid var(--faint2); border-radius:11px; color:var(--dim); font-family:'DM Sans',sans-serif; font-size:0.88rem; cursor:pointer; transition:all 0.2s; }
.modal-cancel:hover { border-color:var(--gold); color:var(--gold); }
.modal-submit { flex:2; padding:0.85rem; background:var(--gold); border:none; border-radius:11px; color:var(--dark); font-family:'Playfair Display',serif; font-size:0.95rem; font-weight:700; cursor:pointer; transition:all 0.2s; }
.modal-submit:hover:not(:disabled) { background:var(--gold-light); }
.modal-submit:disabled { opacity:0.4; cursor:not-allowed; }
.video-loading { text-align:center; padding:2.5rem 1rem; }
.video-loading .dots { margin-bottom:1rem; }
.video-loading-title { font-family:'Playfair Display',serif; font-size:1.05rem; color:var(--gold); margin-bottom:0.5rem; }
.video-loading-text { font-size:0.78rem; color:var(--dim); line-height:1.7; }
.video-error { background:#1a0e0e; border:1px solid rgba(220,80,80,0.3); border-radius:11px; padding:1rem 1.2rem; margin-top:1rem; color:#e07070; font-size:0.82rem; line-height:1.6; }
.optimize-btn { width:100%; margin-top:0.6rem; padding:0.7rem; background:transparent; border:1px dashed rgba(201,168,76,0.45); border-radius:10px; color:var(--gold); font-family:'DM Sans',sans-serif; font-size:0.82rem; cursor:pointer; transition:all 0.2s; }
.optimize-btn:hover:not(:disabled) { background:var(--gold-faint); border-style:solid; }
.optimize-btn:disabled { opacity:0.5; cursor:wait; }

/* Login screen */
.login-wrap { min-height:100vh; display:flex; align-items:center; justify-content:center; padding:2rem; background:var(--dark); }
.login-card { max-width:440px; width:100%; text-align:center; padding:3rem 2.2rem; background:var(--dark-2); border:1px solid var(--faint2); border-radius:20px; }
.login-logo { font-family:'Playfair Display',serif; font-size:2rem; font-weight:900; margin-bottom:0.5rem; }
.login-logo span { color:var(--gold); }
.login-tag { font-size:0.7rem; letter-spacing:0.25em; text-transform:uppercase; color:var(--gold); margin-bottom:1.8rem; }
.login-h1 { font-family:'Playfair Display',serif; font-size:1.8rem; font-weight:900; line-height:1.2; margin-bottom:0.8rem; }
.login-h1 em { font-style:italic; color:var(--gold); }
.login-sub { font-size:0.88rem; color:var(--dim); line-height:1.7; margin-bottom:2rem; }
.login-google { width:100%; padding:1rem; background:var(--gold); border:none; border-radius:12px; color:var(--dark); font-family:'Playfair Display',serif; font-size:1rem; font-weight:700; cursor:pointer; transition:all 0.2s; letter-spacing:0.02em; }
.login-google:hover { background:var(--gold-light); transform:translateY(-2px); }
.login-mention { font-size:0.7rem; color:var(--dim); margin-top:1.2rem; line-height:1.6; }

/* Pricing */
.pricing-grid { display:grid; grid-template-columns:1fr; gap:1rem; margin-top:1.5rem; }
@media (min-width: 720px) { .pricing-grid { grid-template-columns:1fr 1fr; } }
.plan-card { background:var(--dark-3); border:1px solid var(--faint2); border-radius:16px; padding:1.5rem; transition:all 0.2s; }
.plan-card.featured { border-color:rgba(201,168,76,0.5); background:linear-gradient(180deg, var(--gold-faint), var(--dark-3)); }
.plan-label { font-size:0.68rem; letter-spacing:0.22em; text-transform:uppercase; color:var(--gold); margin-bottom:0.4rem; }
.plan-price { font-family:'Playfair Display',serif; font-size:2rem; font-weight:900; line-height:1; margin-bottom:0.2rem; }
.plan-price small { font-size:0.85rem; color:var(--dim); font-weight:400; margin-left:0.2rem; }
.plan-period { font-size:0.72rem; color:var(--dim); margin-bottom:1rem; }
.plan-period b { color:#6fcf97; }
.plan-features { list-style:none; padding:0; margin:1rem 0 1.2rem; font-size:0.83rem; color:var(--white); }
.plan-features li { padding:0.3rem 0; color:var(--dim); }
.plan-features li::before { content:"✦ "; color:var(--gold); margin-right:0.3rem; }
.plan-cta { width:100%; padding:0.85rem; border-radius:11px; font-family:'Playfair Display',serif; font-size:0.95rem; font-weight:700; cursor:pointer; border:none; background:var(--gold); color:var(--dark); transition:all 0.2s; }
.plan-cta:hover:not(:disabled) { background:var(--gold-light); }
.plan-cta.secondary { background:transparent; color:var(--gold); border:1px solid var(--gold); }
.plan-cta:disabled { opacity:0.5; cursor:wait; }
.billing-toggle { display:flex; justify-content:center; gap:0.5rem; margin:1rem 0 0.5rem; }
.billing-toggle button { padding:0.55rem 1.2rem; background:var(--dark-3); border:1px solid var(--faint2); border-radius:100px; color:var(--dim); font-family:'DM Sans',sans-serif; font-size:0.82rem; cursor:pointer; transition:all 0.2s; }
.billing-toggle button.active { background:var(--gold-faint); border-color:rgba(201,168,76,0.5); color:var(--gold); }
.paywall-headline { font-family:'Playfair Display',serif; font-size:1.5rem; color:var(--gold); text-align:center; margin-bottom:0.4rem; }
.paywall-sub { font-size:0.88rem; color:var(--dim); text-align:center; line-height:1.6; margin-bottom:0.5rem; }
.paywall-mention { font-size:0.72rem; color:var(--dim); text-align:center; margin-top:1rem; }
`;

const NETWORKS = [
  { id: "TikTok", icon: "🎵" }, { id: "Instagram", icon: "📸" },
  { id: "LinkedIn", icon: "💼" }, { id: "Twitter/X", icon: "✕" },
];

// ================= LOGIN SCREEN =================
function LoginScreen() {
  const startLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirect = window.location.origin + "/";
    window.location.href = `${EMERGENT_AUTH_URL}/?redirect=${encodeURIComponent(redirect)}`;
  };
  return (
    <>
      <style>{style}</style>
      <div className="login-wrap" data-testid="login-screen">
        <div className="grain" />
        <div className="login-card">
          <div className="login-logo">Ulysse<span>Promo</span></div>
          <div className="login-tag">✦ Marketing IA</div>
          <div className="login-h1">Crée des vidéos <em>virales</em> en 2 minutes</div>
          <div className="login-sub">
            Publie plus, gagne du temps, développe ton audience.<br />
            <b style={{ color: "var(--gold)" }}>2 vidéos d'essai offertes</b>
          </div>
          <button data-testid="login-google-btn" className="login-google" onClick={startLogin}>
            Se connecter avec Google
          </button>
          <div className="login-mention">
            Sans engagement · Résiliation en 1 clic
          </div>
        </div>
      </div>
    </>
  );
}

// ================= PRICING MODAL =================
function PricingModal({ plans, onClose, onCheckout, checkoutLoading, title, subtitle }) {
  const [billing, setBilling] = useState("monthly"); // "monthly" | "yearly"
  if (!plans) return null;
  const creator = plans[billing === "monthly" ? "creator_monthly" : "creator_yearly"];
  const pro = plans[billing === "monthly" ? "pro_monthly" : "pro_yearly"];
  const creatorId = billing === "monthly" ? "creator_monthly" : "creator_yearly";
  const proId = billing === "monthly" ? "pro_monthly" : "pro_yearly";
  return (
    <div className="modal-overlay" data-testid="pricing-modal">
      <div className="modal" style={{ maxWidth: 640 }}>
        <div className="paywall-headline">{title}</div>
        <div className="paywall-sub">{subtitle}</div>

        <div className="billing-toggle">
          <button data-testid="billing-monthly" className={billing === "monthly" ? "active" : ""} onClick={() => setBilling("monthly")}>Mensuel</button>
          <button data-testid="billing-yearly" className={billing === "yearly" ? "active" : ""} onClick={() => setBilling("yearly")}>Annuel · 2 mois offerts</button>
        </div>

        <div className="pricing-grid">
          <div className="plan-card">
            <div className="plan-label">Créateur</div>
            <div className="plan-price">{creator.amount}€<small>/{billing === "monthly" ? "mois" : "an"}</small></div>
            <div className="plan-period">{creator.videos} vidéos · qualité standard</div>
            <ul className="plan-features">
              <li>Scripts illimités</li>
              <li>{creator.videos} vidéos Sora 2 incluses</li>
              <li>Voix-off illimitées</li>
              <li>Téléchargement MP4 + MP3</li>
            </ul>
            <button
              data-testid={`checkout-${creatorId}`}
              className="plan-cta secondary"
              onClick={() => onCheckout(creatorId)}
              disabled={checkoutLoading}
            >
              {checkoutLoading === creatorId ? "Redirection…" : "Commencer maintenant"}
            </button>
          </div>
          <div className="plan-card featured">
            <div className="plan-label">Pro ✦</div>
            <div className="plan-price">{pro.amount}€<small>/{billing === "monthly" ? "mois" : "an"}</small></div>
            <div className="plan-period">{pro.videos} vidéos · <b>HD incluse</b></div>
            <ul className="plan-features">
              <li>Tout Créateur +</li>
              <li>{pro.videos} vidéos dont HD (Sora 2 Pro)</li>
              <li>Priorité de génération</li>
              <li>Toutes fonctionnalités</li>
            </ul>
            <button
              data-testid={`checkout-${proId}`}
              className="plan-cta"
              onClick={() => onCheckout(proId)}
              disabled={checkoutLoading}
            >
              {checkoutLoading === proId ? "Redirection…" : "Passer Pro"}
            </button>
          </div>
        </div>

        <div className="paywall-mention">Sans engagement · Résiliation en 1 clic</div>

        {onClose && (
          <div style={{ textAlign: "center", marginTop: "1rem" }}>
            <button data-testid="pricing-close" className="modal-cancel" onClick={onClose} style={{ maxWidth: 200 }}>
              Plus tard
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ================= MAIN APP =================
function UlyssePromoApp({ user, quota, onLogout, reloadQuota }) {
  const [step, setStep] = useState(1);
  const [url, setUrl] = useState("");
  const [desc, setDesc] = useState("");
  const [networks, setNetworks] = useState([]);
  const [results, setResults] = useState([]);
  const [conseil, setConseil] = useState("");
  const [copied, setCopied] = useState({});
  const [error, setError] = useState("");

  const [videos, setVideos] = useState({});
  const [videoModal, setVideoModal] = useState(null);
  const [videoDuration, setVideoDuration] = useState(8);
  const [videoLoading, setVideoLoading] = useState(false);
  const [videoError, setVideoError] = useState("");
  const [optimizing, setOptimizing] = useState(false);

  const [voiceovers, setVoiceovers] = useState({});
  const [voiceoverModal, setVoiceoverModal] = useState(null);
  const [selectedVoice, setSelectedVoice] = useState("nova");
  const [voiceoverLoading, setVoiceoverLoading] = useState(false);
  const [voiceoverError, setVoiceoverError] = useState("");

  const [pricingOpen, setPricingOpen] = useState(false);
  const [plans, setPlans] = useState(null);
  const [checkoutLoading, setCheckoutLoading] = useState(null);

  const generateRef = useRef(false);
  const videoRef = useRef(false);
  const optimizeRef = useRef(false);
  const voiceoverRef = useRef(false);

  // Load plans on mount
  useEffect(() => {
    fetch(`${API}/plans`).then(r => r.json()).then(d => setPlans(d.plans)).catch(() => {});
  }, []);

  const toggleNetwork = (n) =>
    setNetworks((p) => (p.includes(n) ? p.filter((x) => x !== n) : [...p, n]));

  const isValid = url.trim().length > 5 && networks.length > 0;

  const generate = async () => {
    if (generateRef.current) return;
    generateRef.current = true;
    setStep(2); setError("");
    try {
      const res = await fetch(`${API}/generate`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ url: url.trim(), description: desc.trim(), networks }),
      });
      const data = await readJson(res);
      if (!res.ok) throw new Error(data.detail || `Erreur ${res.status}`);
      const blocks = [];
      if (data.tiktok_scripts && networks.includes("TikTok"))
        data.tiktok_scripts.forEach((s) => blocks.push({ label: `🎵 ${s.title}`, content: s.content }));
      if (data.instagram_posts && networks.includes("Instagram"))
        data.instagram_posts.forEach((p) => blocks.push({ label: `📸 ${p.title}`, content: p.content }));
      if (data.linkedin_posts && networks.includes("LinkedIn"))
        data.linkedin_posts.forEach((p) => blocks.push({ label: `💼 ${p.title}`, content: p.content }));
      if (data.twitter_posts && networks.includes("Twitter/X"))
        data.twitter_posts.forEach((p) => blocks.push({ label: `✕ ${p.title}`, content: p.content }));
      if (data.accroches?.length)
        blocks.push({ label: "⚡ 5 Accroches", content: data.accroches.map((a, i) => `${i + 1}. ${a}`).join("\n") });
      setResults(blocks); setConseil(data.conseil || ""); setStep(3);
    } catch (err) { setError(err.message); setStep(3); }
    finally { generateRef.current = false; }
  };

  const copyText = (text, idx) => {
    navigator.clipboard.writeText(text);
    setCopied((c) => ({ ...c, [idx]: true }));
    setTimeout(() => setCopied((c) => ({ ...c, [idx]: false })), 2000);
  };

  const shareVideo = async (videoUrl, scriptText) => {
    try {
      const res = await fetch(videoUrl); const blob = await res.blob();
      const file = new File([blob], "ulysse-promo.mp4", { type: "video/mp4" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ title: "Ulysse Promo", text: scriptText, files: [file] }); return;
      }
      if (navigator.share) { await navigator.share({ title: "Ulysse Promo", text: scriptText, url: videoUrl }); return; }
      await navigator.clipboard.writeText(`${scriptText}\n\n${videoUrl}`);
      alert("Lien et script copiés !");
    } catch (err) { if (err.name !== "AbortError") console.error(err); }
  };

  const reset = () => {
    setStep(1); setResults([]); setConseil(""); setUrl(""); setDesc(""); setNetworks([]);
    setCopied({}); setError(""); setVideos({}); setVideoModal(null); setVideoError("");
    setVideoLoading(false); setVoiceovers({}); setVoiceoverModal(null);
    setVoiceoverError(""); setVoiceoverLoading(false);
  };

  const openVideoModal = (idx, content) => {
    if (quota.videos_remaining <= 0) { setPricingOpen(true); return; }
    setVideoModal({ blockIdx: idx, prompt: content });
    setVideoDuration(8); setVideoError(""); setOptimizing(false);
  };
  const closeVideoModal = () => { if (videoLoading) return; setVideoModal(null); setVideoError(""); };

  const optimizePrompt = async () => {
    if (!videoModal || optimizeRef.current || videoRef.current) return;
    optimizeRef.current = true; setOptimizing(true); setVideoError("");
    try {
      const res = await fetch(`${API}/optimize-video-prompt`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ script: videoModal.prompt }),
      });
      const data = await readJson(res);
      if (!res.ok) throw new Error(data.detail || `Erreur ${res.status}`);
      setVideoModal((m) => (m ? { ...m, prompt: data.prompt } : m));
    } catch (err) { setVideoError(err.message); }
    finally { optimizeRef.current = false; setOptimizing(false); }
  };

  const generateVideo = async () => {
    if (!videoModal || videoRef.current || optimizeRef.current) return;
    videoRef.current = true; setVideoLoading(true); setVideoError("");
    const blockIdx = videoModal.blockIdx;
    try {
      const res = await authFetch(`${API}/generate-video`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          prompt: videoModal.prompt.trim(), duration: videoDuration,
          size: "720x1280", model: "sora-2",
        }),
      });
      const data = await readJson(res);
      if (res.status === 402) { setVideoModal(null); setPricingOpen(true); return; }
      if (!res.ok) throw new Error(data.detail || `Erreur ${res.status}`);
      const jobId = data.job_id; if (!jobId) throw new Error("job_id manquant");

      const maxAttempts = 72;
      for (let i = 0; i < maxAttempts; i++) {
        await new Promise((r) => setTimeout(r, 5000));
        const sr = await authFetch(`${API}/video-status/${jobId}`);
        const sd = await readJson(sr);
        if (!sr.ok) throw new Error(sd.detail || `Statut ${sr.status}`);
        if (sd.status === "done") {
          setVideos((v) => ({ ...v, [blockIdx]: { url: `${BACKEND_URL}${sd.video_url}`, filename: sd.filename } }));
          setVideoModal(null); await reloadQuota(); return;
        }
        if (sd.status === "error") throw new Error(sd.error || "Génération échouée");
      }
      throw new Error("Dépassement de 6 minutes");
    } catch (err) { setVideoError(err.message); }
    finally { videoRef.current = false; setVideoLoading(false); }
  };

  const openVoiceoverModal = (idx, content) => {
    if (quota.videos_remaining <= 0) { setPricingOpen(true); return; }
    setVoiceoverModal({ blockIdx: idx, text: content });
    setSelectedVoice("nova"); setVoiceoverError("");
  };
  const closeVoiceoverModal = () => { if (voiceoverLoading) return; setVoiceoverModal(null); setVoiceoverError(""); };

  const generateVoiceover = async () => {
    if (!voiceoverModal || voiceoverRef.current) return;
    voiceoverRef.current = true; setVoiceoverLoading(true); setVoiceoverError("");
    try {
      const res = await authFetch(`${API}/generate-voiceover`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text: voiceoverModal.text.trim(), voice: selectedVoice, speed: 1.0 }),
      });
      const data = await readJson(res);
      if (res.status === 402) { setVoiceoverModal(null); setPricingOpen(true); return; }
      if (!res.ok) throw new Error(data.detail || `Erreur ${res.status}`);
      setVoiceovers((v) => ({ ...v, [voiceoverModal.blockIdx]: { url: `${BACKEND_URL}${data.audio_url}`, filename: data.filename } }));
      setVoiceoverModal(null); await reloadQuota();
    } catch (err) { setVoiceoverError(err.message); }
    finally { voiceoverRef.current = false; setVoiceoverLoading(false); }
  };

  const startCheckout = async (packageId) => {
    setCheckoutLoading(packageId);
    try {
      const res = await authFetch(`${API}/checkout/session`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ package_id: packageId, origin_url: window.location.origin }),
      });
      const data = await readJson(res);
      if (!res.ok) throw new Error(data.detail || `Erreur ${res.status}`);
      window.location.href = data.url;
    } catch (err) {
      alert("Erreur paiement : " + err.message);
      setCheckoutLoading(null);
    }
  };

  const quotaLabel = quota.is_trial
    ? (quota.videos_remaining === 1 ? "1 vidéo restante" : `${quota.videos_remaining} vidéos restantes`)
    : `${quota.plan === "pro" ? "PRO" : "CRÉATEUR"} · ${quota.videos_remaining} vidéos`;

  return (
    <>
      <style>{style}</style>
      <div className="app" data-testid="ulysse-app">
        <div className="grain" />

        <header className="header">
          <div>
            <div className="logo" data-testid="brand-logo">Ulysse<span>Promo</span></div>
            <div className="logo-sub">Marketing IA pour vos applications</div>
          </div>
          <div className="header-right">
            <div
              data-testid="quota-badge"
              className={`quota-badge ${quota.videos_remaining <= 0 ? "exhausted" : ""}`}
              onClick={() => setPricingOpen(true)}
              style={{ cursor: "pointer" }}
            >
              {quotaLabel}
            </div>
            {user.picture && <img src={user.picture} alt="" className="avatar" />}
            <button data-testid="logout-btn" className="logout-btn" onClick={onLogout}>Déconnexion</button>
          </div>
        </header>

        <div className="hero">
          <div className="hero-tag">✦ Propulsé par l'IA</div>
          <h1>Transforme ton appli en <em>phénomène viral</em></h1>
          <p>Colle ton URL. Choisis tes réseaux. L'IA génère tout.</p>
          <div className="divider" />
        </div>

        <main className="main">
          {step === 1 && (<>
            <div className="card">
              <div className="card-label">✦ URL de ton application *</div>
              <input data-testid="url-input" placeholder="https://monappli.com" value={url} onChange={e => setUrl(e.target.value)} />
              <div className="hint" style={{ marginTop: "1rem", marginBottom: "0.5rem" }}><b>Description courte</b> (optionnel)</div>
              <textarea data-testid="desc-input" placeholder="Ex : Appli qui aide les Français..." value={desc} onChange={e => setDesc(e.target.value)} />
              <div className="hint">Plus tu décris, plus le contenu sera précis.</div>
            </div>
            <div className="card">
              <div className="card-label">✦ Réseaux à cibler *</div>
              <div className="net-grid">
                {NETWORKS.map(n => (
                  <button key={n.id} data-testid={`network-btn-${n.id.replace("/", "-")}`}
                    className={`net-btn ${networks.includes(n.id) ? "active" : ""}`}
                    onClick={() => toggleNetwork(n.id)}>{n.icon} {n.id}</button>
                ))}
              </div>
            </div>
            <button data-testid="generate-btn" className="gen-btn" onClick={generate} disabled={!isValid}>
              Générer le contenu ✦
            </button>
          </>)}

          {step === 2 && (
            <div className="loading-card" data-testid="loading-card">
              <div className="dots"><div className="dot" /><div className="dot" /><div className="dot" /></div>
              <div className="loading-title">Ulysse génère ton contenu…</div>
              <div className="loading-steps"><b>✦</b> Analyse de l'application<br /><b>✦</b> Création des scripts<br /><b>✦</b> Optimisation réseaux</div>
            </div>
          )}

          {step === 3 && (<>
            {error ? (<>
              <div className="error-card" data-testid="error-card">⚠️ {error}</div>
              <button data-testid="retry-btn" className="reset-btn" onClick={reset}>← Réessayer</button>
            </>) : (<>
              <div className="results-head">
                <div className="results-title" data-testid="results-title">✦ Contenu généré</div>
                <div className="results-sub">Copie, adapte et publie !</div>
              </div>
              {conseil && (
                <div className="conseil-card" data-testid="conseil-card">
                  <div className="conseil-title">💡 Conseil stratégique</div>
                  <div className="conseil-text">{conseil}</div>
                </div>
              )}
              {results.map((r, i) => (
                <div key={i} className="result-block" data-testid={`result-block-${i}`}>
                  <div className="result-head">
                    <div className="result-label">{r.label}</div>
                    <div className="result-head-actions">
                      {r.label.startsWith("🎵") && (<>
                        <button data-testid={`video-btn-${i}`} className="video-btn" onClick={() => openVideoModal(i, r.content)}>🎬 Vidéo</button>
                        <button data-testid={`voiceover-btn-${i}`} className="video-btn" onClick={() => openVoiceoverModal(i, r.content)}>🎤 Voix</button>
                      </>)}
                      <button data-testid={`copy-btn-${i}`} className={`copy-btn ${copied[i] ? "copied" : ""}`} onClick={() => copyText(r.content, i)}>
                        {copied[i] ? "✓ Copié" : "Copier"}
                      </button>
                    </div>
                  </div>
                  <div className="result-content">{r.content}</div>
                  {videos[i] && (
                    <div className="video-embed" data-testid={`video-embed-${i}`}>
                      <video src={videos[i].url} controls playsInline data-testid={`video-player-${i}`} />
                      <a href={videos[i].url} download={videos[i].filename} data-testid={`video-download-${i}`}>⬇ Télécharger</a>
                      <button className="share-btn" data-testid={`video-share-${i}`} onClick={() => shareVideo(videos[i].url, r.content)}>📤 Partager</button>
                      <span className="share-note">Astuce : ouvre CapCut pour ajouter voix + musique.</span>
                    </div>
                  )}
                  {voiceovers[i] && (
                    <div className="video-embed" data-testid={`voiceover-embed-${i}`}>
                      <audio src={voiceovers[i].url} controls data-testid={`voiceover-player-${i}`}
                        style={{ width: "100%", maxWidth: "320px", display: "block", margin: "0 auto 0.8rem" }} />
                      <a href={voiceovers[i].url} download={voiceovers[i].filename} data-testid={`voiceover-download-${i}`}>⬇ Télécharger MP3</a>
                    </div>
                  )}
                </div>
              ))}
              <button data-testid="reset-btn" className="reset-btn" onClick={reset}>← Générer pour une autre appli</button>
            </>)}
          </>)}
        </main>

        <footer className="footer">ULYSSE PROMO — Propulse ton appli sur les réseaux ✦</footer>

        {videoModal && (
          <div className="modal-overlay" data-testid="video-modal">
            <div className="modal">
              <div className="modal-title">🎬 Générer une vidéo IA</div>
              <div className="modal-sub">Sora 2 · Format vertical 9:16</div>
              <div className="modal-label">✦ Prompt (éditable)</div>
              <textarea data-testid="video-prompt-input" value={videoModal.prompt}
                onChange={(e) => setVideoModal({ ...videoModal, prompt: e.target.value })}
                disabled={videoLoading || optimizing} />
              <button data-testid="optimize-prompt-btn" className="optimize-btn" onClick={optimizePrompt}
                disabled={videoLoading || optimizing || !videoModal.prompt.trim()}>
                {optimizing ? "✨ Optimisation…" : "✨ Optimiser le prompt pour Sora (recommandé)"}
              </button>
              <div className="modal-label">✦ Durée</div>
              <div className="duration-grid">
                {[4, 8, 12].map((d) => (
                  <button key={d} data-testid={`duration-btn-${d}`}
                    className={`duration-btn ${videoDuration === d ? "active" : ""}`}
                    onClick={() => setVideoDuration(d)} disabled={videoLoading}>{d} sec</button>
                ))}
              </div>
              {videoLoading ? (
                <div className="video-loading" data-testid="video-loading">
                  <div className="dots"><div className="dot" /><div className="dot" /><div className="dot" /></div>
                  <div className="video-loading-title">Sora 2 crée ta vidéo…</div>
                  <div className="video-loading-text">
                    <b style={{ color: "var(--gold)" }}>1 à 3 minutes</b>. Ne ferme pas cette fenêtre.
                  </div>
                </div>
              ) : (<>
                {videoError && <div className="video-error" data-testid="video-error">⚠️ {videoError}</div>}
                <div className="modal-actions">
                  <button data-testid="video-cancel-btn" className="modal-cancel" onClick={closeVideoModal}>Annuler</button>
                  <button data-testid="video-submit-btn" className="modal-submit" onClick={generateVideo}
                    disabled={!videoModal.prompt.trim()}>Générer la vidéo ✦</button>
                </div>
              </>)}
            </div>
          </div>
        )}

        {voiceoverModal && (
          <div className="modal-overlay" data-testid="voiceover-modal">
            <div className="modal">
              <div className="modal-title">🎤 Générer une voix-off</div>
              <div className="modal-sub">OpenAI TTS · MP3 prêt pour CapCut</div>
              <div className="modal-label">✦ Texte (éditable)</div>
              <textarea data-testid="voiceover-text-input" value={voiceoverModal.text}
                onChange={(e) => setVoiceoverModal({ ...voiceoverModal, text: e.target.value })}
                disabled={voiceoverLoading} />
              <div className="modal-label">✦ Voix</div>
              <div className="duration-grid" style={{ gridTemplateColumns: "repeat(2,1fr)" }}>
                {[
                  { id: "nova", label: "Nova · Énergique" },
                  { id: "shimmer", label: "Shimmer · Joyeuse" },
                  { id: "onyx", label: "Onyx · Profonde" },
                  { id: "sage", label: "Sage · Posée" },
                ].map((v) => (
                  <button key={v.id} data-testid={`voice-btn-${v.id}`}
                    className={`duration-btn ${selectedVoice === v.id ? "active" : ""}`}
                    onClick={() => setSelectedVoice(v.id)} disabled={voiceoverLoading}>{v.label}</button>
                ))}
              </div>
              {voiceoverLoading ? (
                <div className="video-loading" data-testid="voiceover-loading">
                  <div className="dots"><div className="dot" /><div className="dot" /><div className="dot" /></div>
                  <div className="video-loading-title">Génération de la voix…</div>
                  <div className="video-loading-text"><b style={{ color: "var(--gold)" }}>5 à 15 secondes</b></div>
                </div>
              ) : (<>
                {voiceoverError && <div className="video-error" data-testid="voiceover-error">⚠️ {voiceoverError}</div>}
                <div className="modal-actions">
                  <button data-testid="voiceover-cancel-btn" className="modal-cancel" onClick={closeVoiceoverModal}>Annuler</button>
                  <button data-testid="voiceover-submit-btn" className="modal-submit" onClick={generateVoiceover}
                    disabled={!voiceoverModal.text.trim()}>Générer la voix ✦</button>
                </div>
              </>)}
            </div>
          </div>
        )}

        {pricingOpen && (
          <PricingModal
            plans={plans}
            onClose={quota.videos_remaining > 0 ? () => setPricingOpen(false) : null}
            onCheckout={startCheckout}
            checkoutLoading={checkoutLoading}
            title={quota.videos_remaining <= 0 ? "Votre essai est terminé 🎉" : "Crée des vidéos virales en 2 minutes"}
            subtitle={quota.videos_remaining <= 0
              ? "Passez à l'abonnement pour continuer à créer des vidéos"
              : "Publie plus, gagne du temps, développe ton audience"}
          />
        )}
      </div>
    </>
  );
}

// ================= PAYMENT SUCCESS (polling) =================
function PaymentSuccessScreen({ onDone }) {
  const [status, setStatus] = useState("checking");
  const [attempts, setAttempts] = useState(0);
  const processedRef = useRef(false);

  useEffect(() => {
    if (processedRef.current) return;
    processedRef.current = true;
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) { setStatus("error"); return; }

    const poll = async (attempt = 0) => {
      if (attempt >= 10) { setStatus("timeout"); return; }
      try {
        const res = await authFetch(`${API}/checkout/status/${sessionId}`);
        const data = await readJson(res);
        if (data.payment_status === "paid") { setStatus("paid"); setTimeout(onDone, 1500); return; }
        if (data.status === "expired") { setStatus("expired"); return; }
        setAttempts(attempt + 1);
        setTimeout(() => poll(attempt + 1), 2000);
      } catch { setTimeout(() => poll(attempt + 1), 2000); }
    };
    poll();
  }, [onDone]);

  return (
    <>
      <style>{style}</style>
      <div className="login-wrap" data-testid="payment-success">
        <div className="login-card">
          <div className="login-logo">Ulysse<span>Promo</span></div>
          {status === "checking" && (<>
            <div className="dots"><div className="dot" /><div className="dot" /><div className="dot" /></div>
            <div className="login-h1">Vérification du paiement…</div>
            <div className="login-sub">Tentative {attempts + 1}/10</div>
          </>)}
          {status === "paid" && (<>
            <div className="login-h1">✦ Paiement confirmé !</div>
            <div className="login-sub">Ton abonnement est activé. Redirection…</div>
          </>)}
          {status === "expired" && (<>
            <div className="login-h1">Session expirée</div>
            <div className="login-sub">Recommence le paiement depuis l'app.</div>
            <button className="login-google" onClick={onDone}>Retour à l'app</button>
          </>)}
          {status === "error" && (<>
            <div className="login-h1">Erreur</div>
            <button className="login-google" onClick={onDone}>Retour à l'app</button>
          </>)}
          {status === "timeout" && (<>
            <div className="login-h1">Vérification trop longue</div>
            <div className="login-sub">Ton paiement sera confirmé bientôt. Reconnecte-toi dans 1 minute.</div>
            <button className="login-google" onClick={onDone}>Retour à l'app</button>
          </>)}
        </div>
      </div>
    </>
  );
}

// ================= ROOT =================
export default function App() {
  const [authState, setAuthState] = useState({ loading: true, user: null, quota: null });
  const [view, setView] = useState("app"); // "app" | "payment-success"
  const sessionProcessedRef = useRef(false);

  const checkAuth = useCallback(async () => {
    try {
      const res = await authFetch(`${API}/auth/me`);
      if (!res.ok) throw new Error("Not auth");
      const data = await readJson(res);
      setAuthState({ loading: false, user: data.user, quota: data.quota });
    } catch {
      setAuthState({ loading: false, user: null, quota: null });
    }
  }, []);

  const reloadQuota = useCallback(async () => {
    try {
      const res = await authFetch(`${API}/auth/me`);
      if (!res.ok) return;
      const data = await readJson(res);
      setAuthState((s) => ({ ...s, quota: data.quota }));
    } catch {}
  }, []);

  // Handle OAuth callback (URL fragment) + payment success redirect
  useEffect(() => {
    // 1) OAuth callback — must run BEFORE auth check
    if (window.location.hash.includes("session_id=") && !sessionProcessedRef.current) {
      sessionProcessedRef.current = true;
      const sessionId = window.location.hash.split("session_id=")[1].split("&")[0];
      const fp = getDeviceFingerprint();
      fetch(`${API}/auth/session`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, device_fingerprint: fp }),
      })
        .then(async (r) => {
          const d = await readJson(r);
          if (!r.ok) throw new Error(d.detail || "Auth failed");
          if (d.session_token) localStorage.setItem("session_token", d.session_token);
          setAuthState({ loading: false, user: d.user, quota: d.quota });
          window.history.replaceState(null, "", window.location.pathname);
        })
        .catch((err) => {
          alert("Échec de connexion : " + err.message);
          setAuthState({ loading: false, user: null, quota: null });
          window.history.replaceState(null, "", window.location.pathname);
        });
      return;
    }

    // 2) Payment success route
    if (window.location.pathname === "/payment-success") {
      setView("payment-success");
      checkAuth();
      return;
    }

    // 3) Normal auth check
    checkAuth();
  }, [checkAuth]);

  const handleLogout = async () => {
    try { await authFetch(`${API}/auth/logout`, { method: "POST" }); } catch {}
    localStorage.removeItem("session_token");
    setAuthState({ loading: false, user: null, quota: null });
  };

  const handlePaymentDone = async () => {
    window.history.replaceState(null, "", "/");
    setView("app");
    await checkAuth();
  };

  if (authState.loading) {
    return (
      <>
        <style>{style}</style>
        <div className="login-wrap"><div className="login-card">
          <div className="dots"><div className="dot" /><div className="dot" /><div className="dot" /></div>
        </div></div>
      </>
    );
  }

  if (view === "payment-success") {
    return <PaymentSuccessScreen onDone={handlePaymentDone} />;
  }

  if (!authState.user) return <LoginScreen />;

  return (
    <UlyssePromoApp
      user={authState.user}
      quota={authState.quota}
      onLogout={handleLogout}
      reloadQuota={reloadQuota}
    />
  );
}
